/* eslint-disable @typescript-eslint/no-unused-vars */
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import { RootState } from './store'
import { File, setFile } from './file'
import { obtainErrorInfo } from './rtkErrorLogger'
import { Auth } from './auth'
import { App, setChat, setNotificationsAvailable } from './root'
import {
  CollabFile,
  CollabFileGoogle,
  CollabFileMS,
  setCollabFile,
} from './collabFile'
import { generateResourceMap } from '../helpers'

export type ShareFile = ShareFileGoogle | ShareFileMS

export type ShareFileGoogle = { drive: App } & {
  fileId: string
  accessType: string
  screenName: string
  collaborationId: string
} & CollabFileGoogle

export type ShareFileMS = { drive: App } & {
  fileId: string
  accessType: string
  screenName: string
  collaborationId: string
} & CollabFileMS

const baseQuery = retry(
  fetchBaseQuery({
    baseUrl: `/api/`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState
      const zoomAppContext = state.root.appContext

      if (zoomAppContext) {
        headers.set('x-zoom-app-context', zoomAppContext)
      }

      return headers
    },
  }),
  {
    retryCondition: (error, _, { attempt }) => {
      const { status, message } = obtainErrorInfo(error)
      // if a user does not get consent for tracking, /metric will return a 403
      if (status === 403 && message === 'User has not granted consent')
        return false
      // this is returned by the /file endpoint if the user does not have permissions to the file
      if (status === 403 && message === 'No file permissions') return false

      if (attempt > 4) return false

      return true
    },
  }
)

export const appApi = createApi({
  reducerPath: 'appApi',
  baseQuery,
  tagTypes: ['Auth'], // these help invalidate data in the store to trigger auto-refetch when a mutation would invalidate the cache for a query
  endpoints: (builder) => ({
    isFileSharable: builder.query<
      { shareable: boolean },
      { drive: App } & { fileId: string; fileDriveId?: string }
    >({
      query: ({ drive, ...paramsObj }) => ({
        url: `${drive.toLowerCase()}/file/shareable`,
        method: 'GET',
        params: paramsObj,
      }),
    }),
    login: builder.query<{ data: Auth }, App>({
      query: (drive) => ({
        url: `connectors/${drive.toLowerCase()}/auth_status`,
        method: 'GET',
      }),
      transformResponse: (response: { data: Auth }, _, app) => {
        const { authStatus, extra } = response.data

        if (authStatus && extra.resource && app === 'Microsoft') {
          const msResourceTokens = generateResourceMap(
            extra.resource
          ) as Record<string, string>

          const transformedResponse = {
            ...response,
            data: {
              ...response.data,
              extra: {
                ...response.data.extra,
                resource: msResourceTokens,
              },
            },
          }
          return transformedResponse
        } else {
          return response
        }
      },
      providesTags: ['Auth'],
    }),
    requiresEmailConsent: builder.query<{ needsEmailConsent: boolean }, App>({
      query: (drive) => ({
        url: `${drive.toLowerCase()}/requires_email_consent`,
        method: 'GET',
      }),
      keepUnusedDataFor: Infinity,
    }),
    logout: builder.mutation<
      {
        code: string
        message: string
        body: boolean
        success: boolean
        serverError: boolean
      },
      App
    >({
      query: (drive) => ({
        url: `connectors/${drive.toLowerCase()}/disconnect`,
        method: 'GET',
      }),
      invalidatesTags: ['Auth'],
    }),
    sharerDetails: builder.query<
      | { sharerScreenName: string; isSharer: boolean }
      | ({
          isSharer: true
          sharerScreenName: string
        } & CollabFile),
      App
    >({
      query: (drive) => ({
        url: `${drive.toLowerCase()}/sharer_details`,
        method: 'GET',
      }),
      keepUnusedDataFor: Infinity,
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        // allows the resp to be directly placed into the store
        const { data } = await queryFulfilled
        // if we are the sharer we also get the collab file info here
        if (data.isSharer) {
          const { isSharer, sharerScreenName, ...rest } = data
          const collabFile = rest as CollabFile
          dispatch(setCollabFile(collabFile))
        }
      },
    }),
    shareFile: builder.mutation<CollabFile, ShareFile>({
      query: ({ drive, ...bodyObj }) => ({
        url: `${drive.toLowerCase()}/share_file`,
        method: 'POST',
        body: bodyObj,
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          // allows the resp to be directly placed into the store
          const { data } = await queryFulfilled
          dispatch(setCollabFile(data))
        } catch {
          // swallow as global rtkErrorLogger manages
        }
      },
    }),
    createFileGoogle: builder.mutation<
      CollabFileGoogle & { fileId: string },
      {
        fileName: CollabFile['fileName']
        fileType: CollabFile['fileType']
      } & { share: boolean }
    >({
      query: (bodyObj) => ({
        url: `google/file/create`,
        method: 'POST',
        body: bodyObj,
      }),
      onQueryStarted: async ({ share }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          if (share) {
            // allows the resp to be directly placed into the store
            dispatch(setCollabFile(data))
          }
        } catch {
          // swallow
        }
      },
    }),
    createFileMS: builder.mutation<
      CollabFileMS,
      {
        fileName: CollabFile['fileName']
        fileType: CollabFile['fileType']
        driveId: CollabFileMS['driveId']
      }
    >({
      query: (bodyObj) => ({
        url: `microsoft/file/create`,
        method: 'POST',
        body: bodyObj,
      }),
      onQueryStarted: async ({ driveId }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        dispatch(
          setFile({
            url: data?.fileUrl,
            id: data?.fileId,
            siteUrl: data?.siteUrl,
            name: data?.fileName,
            type: data?.fileType,
            resourceUrl: data?.resourceUrl,
            driveId,
          })
        )
      },
    }),
    createFilePermission: builder.mutation<CollabFile, App>({
      query: (drive) => ({
        url: `${drive.toLowerCase()}/file/permission`,
        method: 'POST',
      }),
      onQueryStarted: async (app, { dispatch, queryFulfilled }) => {
        try {
          // allows the resp to be directly placed into the store
          const { data } = await queryFulfilled
          dispatch(setCollabFile(data))
          // if we made it this far we were successful
          // manually update the needsEmailConsent
          dispatch(
            appApi.util.updateQueryData('requiresEmailConsent', app, () => {
              return { needsEmailConsent: false }
            })
          )
        } catch {
          // swallow as global rtkErrorLogger manages
        }
      },
    }),
    unfurlFile: builder.query<File, { drive: App } & { url: string }>({
      query: ({ drive, ...paramsObj }) => ({
        url: `${drive.toLowerCase()}/file`,
        method: 'GET',
        params: paramsObj,
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          // allows the resp to be directly placed into the store
          const { data } = await queryFulfilled
          dispatch(setFile(data))
        } catch {
          // swallow as global rtkErrorLogger manages
        }
      },
    }),
    getSubscriptions: builder.query<
      { chat: boolean; notificationsAvailable: boolean },
      App
    >({
      query: (drive) => ({
        url: `${drive.toLowerCase()}/subscription`,
        method: 'GET',
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          // allows the resp to be directly placed into the store
          const { data } = await queryFulfilled
          dispatch(setChat(data.chat))
          dispatch(setNotificationsAvailable(data.notificationsAvailable))
        } catch {
          // swallow as global rtkErrorLogger manages
        }
      },
    }),
    subscribe: builder.mutation<
      { chat: boolean; notificationsAvailable: boolean },
      App
    >({
      query: (drive) => ({
        url: `${drive.toLowerCase()}/subscription`,
        method: 'POST',
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(setChat(data.chat))
          dispatch(setNotificationsAvailable(data.notificationsAvailable))
        } catch (err) {
          dispatch(setChat(false))
        }
      },
    }),
    unsubscribe: builder.mutation<Response, App>({
      query: (drive) => ({
        url: `${drive.toLowerCase()}/subscription`,
        method: 'DELETE',
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        // allows the resp to be directly placed into the store
        dispatch(setChat(false))

        try {
          await queryFulfilled
        } catch (err) {
          dispatch(setChat(true))
        }
      },
    }),
    getUserAvatar: builder.query<{ avatar?: string }, App>({
      query: (drive) => ({
        url: `${drive.toLowerCase()}/avatar`,
        method: 'GET',
      }),
    }),
    trackMetric: builder.mutation<
      { success: boolean; message: string },
      {
        measure: string
        tag: Record<string, string>
        fields: Record<string, boolean | number | string>
      }
    >({
      query: (bodyObj) => ({
        url: 'zoomapp/metric',
        method: 'POST',
        body: bodyObj,
      }),
    }),
  }),
})

export const {
  useLazyIsFileSharableQuery,
  useGetSubscriptionsQuery,
  useSubscribeMutation,
  useUnsubscribeMutation,
  useGetUserAvatarQuery,
  useLoginQuery,
  useLazyLoginQuery,
  useLogoutMutation,
  useShareFileMutation,
  useCreateFileGoogleMutation,
  useSharerDetailsQuery,
  useCreateFilePermissionMutation,
  useRequiresEmailConsentQuery,
  useLazySharerDetailsQuery,
  useUnfurlFileQuery,
  useCreateFileMSMutation,
  useTrackMetricMutation,
} = appApi
